import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  CardHeader,
  Theme,
  Typography,
} from "@material-ui/core";
import { pageTheme } from "@backstage/theme";
import { DatadogSloSection } from "../Datadog/DatadogSloSection";
import { HoneybadgerFaultsSection } from "../Honeybadger/HoneybadgerFaultsSection/HoneybadgerFaultsSection";
import { Spacer } from "@internal/plugin-utilities";
import { Progress } from "@backstage/core";
import { Entity } from "@backstage/catalog-model";
import { JsonObject } from "@backstage/types";
import { IncidentsTable } from "../../IncidentsTable";
import ExpandMore from "@material-ui/icons/ExpandMore";
import { TeamBreakdownHeader } from "../TeamBreakdownHeader";
// import { Ownership } from "../Ownership/Ownership";

const useStyles = makeStyles((_theme: Theme) => ({
  container: {
    margin: "0 100px",
    minHeight: 300,
  },
  content: {
    padding: "20px",
  },
}));

interface Props {
  team: Entity;
  data: any;
  loading: boolean;
  disabled?: boolean;
}

export const TeamCarouselItem: React.FC<Props> = ({
  team,
  data,
  loading,
  disabled,
}: Props): JSX.Element => {
  const classes = useStyles();
  const [items, setItems] = useState<any>();
  const [expanded, setExpanded] = React.useState<string | false>(
    "open-incidents"
  );

  const handleAccordionChange =
    (panel: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  useEffect(() => {
    const slos = data.datadog.slos;
    const faults = data.honeybadger.faults;
    const opsgenieIncidents = data.opsgenie.incidents;
    const datadogIncidents = data.datadog?.incidents || {
      data: [],
      violation: false,
    };
    // const ownership = data.kajabi.ownership;

    const jsx = [];

    // if (ownership && ownership.violation) {
    //   jsx.push(
    //     <Ownership
    //       ownershipData={ownership}
    //       loading={loading}
    //       key="ownership"
    //     />
    //   );
    //   jsx.push(<Spacer variant="double" key="ownership-spacer" />);
    // }

    if (slos.violation) {
      jsx.push(<DatadogSloSection data={slos} loading={loading} key="slos" />);
      jsx.push(<Spacer variant="double" key="slos-spacer" />);
    }

    if (faults.violation) {
      jsx.push(
        <HoneybadgerFaultsSection
          data={faults}
          loading={loading}
          key="honeybadger"
        />
      );
      jsx.push(<Spacer variant="double" key="honeybadger-spacer" />);
    }

    // Show incidents section if either OpsGenie or Datadog incidents exist
    if (opsgenieIncidents.data.length > 0 || datadogIncidents.data.length > 0) {
      const tooltip = (
        <>
          <Typography variant="h5">Incidents</Typography>
          <div>
            <p>
              This section displays OpsGenie and Datadog incidents over a three
              week span. This will allow us to view these incidents and allow
              enough time for teams to finish up post mortems. It is expected to
              discuss the executive summary of the incident and only go into
              fine details if questions are asked.
            </p>

            <p>
              A <code>Reviewed</code> checkbox accompanies all incidents to
              allow us to determine which items we've already discussed.
            </p>
          </div>
        </>
      );

      jsx.push(
        <TeamBreakdownHeader
          vendor="opsgenie" // Keep opsgenie as the primary vendor for header styling
          title="Incidents"
          tooltip={tooltip}
          key="incidents-title"
        />
      );
      jsx.push(
        <Accordion
          defaultExpanded
          expanded={expanded === "open-incidents"}
          onChange={handleAccordionChange("open-incidents")}
          style={{ marginBottom: "0px" }}
          key="open-incidents-accordion"
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            style={{ backgroundColor: "#333333", borderRadius: "5px" }}
          >
            <Typography variant="h3" style={{ width: "100%" }} align="center">
              📬 Open Incidents
            </Typography>
          </AccordionSummary>
          <AccordionDetails style={{ width: "100%", display: "block" }}>
            <IncidentsTable
              opsgenieIncidents={opsgenieIncidents.data}
              datadogIncidents={datadogIncidents.data}
              key="open-incidents"
              disabled={disabled}
              reviewed={false}
            />
          </AccordionDetails>
        </Accordion>
      );
      jsx.push(
        <Accordion
          expanded={expanded === "reviewed-incidents"}
          onChange={handleAccordionChange("reviewed-incidents")}
          key="reviewed-incidents-accordion"
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            style={{ backgroundColor: "#333333", borderRadius: "5px" }}
          >
            <Typography variant="h3" style={{ width: "100%" }} align="center">
              ✅ Already Reviewed Incidents
            </Typography>
          </AccordionSummary>
          <AccordionDetails style={{ width: "100%", display: "block" }}>
            <IncidentsTable
              opsgenieIncidents={opsgenieIncidents.data}
              datadogIncidents={datadogIncidents.data}
              key="reviewed-incidents"
              disabled={disabled}
              reviewed
            />
          </AccordionDetails>
        </Accordion>
      );
      jsx.push(<Spacer variant="double" key="incidents-spacer" />);
    }

    setItems(jsx);
  }, [data, loading, disabled, expanded]);

  if (loading) {
    return (
      <Card className={classes.container}>
        <Progress />
      </Card>
    );
  }

  return (
    <Card className={classes.container}>
      <CardHeader
        title={
          ((team?.spec?.profile as JsonObject)?.displayName ||
            (team?.spec?.profile as JsonObject)?.displayname ||
            team.metadata.name) as string
        }
        style={{
          backgroundImage: pageTheme.apis.backgroundImage,
          backgroundSize: "cover",
          color: "white",
        }}
      />
      <CardContent>{items}</CardContent>
    </Card>
  );
};
